import { useServices } from "@/services/hooks/useServices";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

export const useGetSenderStats = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["sender-stats", { startDate, endDate }],
    queryFn: () =>
      statisticsService.getSenderStatistics({
        startDate,
        endDate,
      }),
    refetchOnWindowFocus: false,
  });
};

export const useGetDailyStats = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["daily-stats", { startDate, endDate }],
    queryFn: () =>
      statisticsService.getDailyStatistics({
        startDate,
        endDate,
      }),
    refetchOnWindowFocus: false,
  });
};

export const useGetCategoryStats = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["category-stats", { startDate, endDate }],
    queryFn: () =>
      statisticsService.getCategoryStatistics({
        startDate,
        endDate,
      }),
    refetchOnWindowFocus: false,
  });
};

export const useGetInteractionsRangeStats = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["interactions-range-stats", { startDate, endDate }],
    queryFn: () =>
      statisticsService.getInteractionsRangeStatistics({
        startDate,
        endDate,
      }),
    refetchOnWindowFocus: false,
  });
};

// Fetch hostnames hook
export const useFetchHostnames = () => {
  const { statisticsService } = useServices();
  return useMutation({
    mutationFn: (data: {
      ips: string[];
      startDate: string;
      endDate: string;
    }) => statisticsService.fetchHostnames(data),
  });
};

// Get senders with templates hook
export const useGetSendersWithTemplates = ({
  startDate,
  endDate,
  enabled = false,
}: {
  startDate: string;
  endDate: string;
  enabled?: boolean;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["senders-with-templates", { startDate, endDate }],
    queryFn: () =>
      statisticsService.getSendersWithTemplates({
        startDate,
        endDate,
      }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

// Get templates for specific sender hook
export const useGetSenderTemplates = ({
  senderEmail,
  startDate,
  endDate,
  enabled = false,
}: {
  senderEmail: string;
  startDate: string;
  endDate: string;
  enabled?: boolean;
}) => {
  const { statisticsService } = useServices();

  return useQuery({
    queryKey: ["sender-templates", { senderEmail, startDate, endDate }],
    queryFn: () =>
      statisticsService.getSenderTemplates(senderEmail, {
        startDate,
        endDate,
      }),
    enabled: enabled && !!senderEmail,
    refetchOnWindowFocus: false,
  });
};

export type DownloadFailedHeadersParams = {
  date: string;
  spfResult?: boolean;
  dkimResult?: boolean;
  dmarcResult?: boolean;
};

export const useDownloadFailedHeaders = () => {
  const { statisticsService } = useServices();
  return useMutation({
    mutationFn: (params: DownloadFailedHeadersParams) =>
      statisticsService.downloadFailedHeaders(params),
  });
};


